export interface ShipmentUploadIdentifierRow {
  reference_number?: unknown;
  container_number?: unknown;
  awb_number?: unknown;
  booking_awb?: unknown;
  row_number?: number | null;
}

export interface ShipmentUploadIdentifierConflict {
  reference_number: string;
  row_numbers: number[];
  container_numbers: string[];
  awb_numbers: string[];
}

const AWB_NUMBER_REGEX = /^(\d{3})-?(\d{8})$/;

export const normalizeShipmentReference = (value: unknown): string =>
  String(value ?? '').trim();

export const isValidShipmentReference = (value: unknown): boolean => {
  const reference = normalizeShipmentReference(value);
  return (
    reference !== '' &&
    reference !== 'SIN REFERENCIA' &&
    reference !== '__SIN_REFERENCIA__'
  );
};

export const hasShipmentUploadIdentifier = (
  row: Pick<ShipmentUploadIdentifierRow, 'container_number' | 'awb_number'>
): boolean =>
  [row.container_number, row.awb_number].some(
    (value) => String(value ?? '').trim() !== ''
  );

const normalizeContainerNumber = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, '');

const normalizeAwbNumber = (value: unknown): string | null => {
  const normalized = String(value ?? '')
    .trim()
    .replace(/\s/g, '');
  if (!normalized) return null;

  const match = normalized.match(AWB_NUMBER_REGEX);
  return match ? `${match[1]}-${match[2]}` : normalized.toUpperCase();
};

const containerNumbersFromValue = (value: unknown): string[] =>
  String(value ?? '')
    .split(/[;,]+/)
    .map(normalizeContainerNumber)
    .filter(Boolean);

const awbNumbersFromRow = (row: ShipmentUploadIdentifierRow): string[] => {
  const awbs: string[] = [];
  const explicitAwb = normalizeAwbNumber(row.awb_number);
  if (explicitAwb) awbs.push(explicitAwb);

  // booking_awb is a legacy mixed field. Ordinary booking values should not
  // participate in AWB conflict validation.
  const legacyValue = String(row.booking_awb ?? '')
    .trim()
    .replace(/\s/g, '');
  if (AWB_NUMBER_REGEX.test(legacyValue)) {
    const legacyAwb = normalizeAwbNumber(legacyValue);
    if (legacyAwb) awbs.push(legacyAwb);
  }

  return awbs;
};

/**
 * A CherryMarket reference identifies one operational shipment. Repeated box
 * rows are valid only when they all resolve to the same container or AWB.
 */
export const findShipmentUploadIdentifierConflicts = (
  rows: ShipmentUploadIdentifierRow[]
): ShipmentUploadIdentifierConflict[] => {
  const groups = new Map<
    string,
    { containers: Set<string>; awbs: Set<string>; rowNumbers: Set<number> }
  >();

  for (const row of rows) {
    const reference = normalizeShipmentReference(row.reference_number);
    if (!reference) continue;

    const group = groups.get(reference) ?? {
      containers: new Set<string>(),
      awbs: new Set<string>(),
      rowNumbers: new Set<number>(),
    };

    for (const container of containerNumbersFromValue(row.container_number)) {
      group.containers.add(container);
    }
    for (const awb of awbNumbersFromRow(row)) {
      group.awbs.add(awb);
    }
    if (typeof row.row_number === 'number' && row.row_number > 0) {
      group.rowNumbers.add(row.row_number);
    }

    groups.set(reference, group);
  }

  return [...groups.entries()]
    .filter(
      ([, group]) =>
        group.containers.size > 1 ||
        group.awbs.size > 1 ||
        (group.containers.size > 0 && group.awbs.size > 0)
    )
    .map(([reference, group]) => ({
      reference_number: reference,
      row_numbers: [...group.rowNumbers].sort((left, right) => left - right),
      container_numbers: [...group.containers].sort(),
      awb_numbers: [...group.awbs].sort(),
    }))
    .sort((left, right) =>
      left.reference_number.localeCompare(right.reference_number)
    );
};
